import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCalculator, FaRulerVertical, FaWeight } from 'react-icons/fa';

const bmiCategories = [
  { label: 'Underweight', range: 'Below 18.5', color: '#3498db', min: 0, max: 18.5 },
  { label: 'Normal', range: '18.5 – 24.9', color: '#27ae60', min: 18.5, max: 24.9 },
  { label: 'Overweight', range: '25 – 29.9', color: '#f39c12', min: 25, max: 29.9 },
  { label: 'Obese', range: '30 and above', color: '#e51b24', min: 30, max: 100 },
];

const getBmiCategory = (bmi) => {
  return bmiCategories.find((cat) => bmi >= cat.min && bmi < cat.max) || bmiCategories[3];
};

const getGaugePercent = (bmi) => {
  const clamped = Math.min(Math.max(bmi, 10), 40);
  return ((clamped - 10) / 30) * 100;
};

const BmiCalculator = () => {
  const [height, setHeight] = useState('');
  const [weight, setWeight] = useState('');
  const [bmi, setBmi] = useState(null);
  const [category, setCategory] = useState(null);
  const [error, setError] = useState('');

  const transition = { type: 'spring', duration: 2.5 };

  const calculate = () => {
    const h = parseFloat(height);
    const w = parseFloat(weight);
    if (!h || !w || h <= 0 || w <= 0) {
      setError('Please enter valid height and weight values.');
      setBmi(null);
      setCategory(null);
      return;
    }
    if (h < 50 || h > 250) {
      setError('Please enter height between 50 cm and 250 cm.');
      return;
    }
    if (w < 10 || w > 300) {
      setError('Please enter weight between 10 kg and 300 kg.');
      return;
    }
    setError('');
    const heightM = h / 100;
    const result = w / (heightM * heightM);
    const rounded = Math.round(result * 10) / 10;
    setBmi(rounded);
    setCategory(getBmiCategory(rounded));
  };

  const reset = () => {
    setHeight('');
    setWeight('');
    setBmi(null);
    setCategory(null);
    setError('');
  };

  return (
    <section className="relative px-4 md:px-12 py-16 text-white overflow-hidden max-w-7xl mx-auto" id="bmi">
      <div className="absolute w-72 h-72 bg-red-600/15 blur-[150px] rounded-full top-10 left-10 -z-10" />

      {/* Header */}
      <motion.div
        className="text-center max-w-2xl mx-auto mb-16"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={transition}
        viewport={{ once: true }}
      >
        <span className="inline-block bg-red-600/15 border border-red-500/30 text-red-500 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-3 shadow-lg">
          Health & Assessment
        </span>
        <div className="text-3xl sm:text-5xl font-black uppercase tracking-wide mb-4">
          <span className="text-transparent [-webkit-text-stroke:_1.5px_white]">BMI </span>
          <span className="text-red-500">Calculator</span>
        </div>
        <p className="text-gray-300 text-xs sm:text-sm leading-relaxed">
          Calculate your Body Mass Index and get a personalised fitness recommendation for your transformation.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* Form Card */}
        <motion.div
          className="bg-gradient-to-b from-[#181a24]/90 to-[#12141c]/90 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl flex flex-col justify-between"
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={transition}
          viewport={{ once: true }}
        >
          <div>
            <div className="flex items-center gap-3 text-red-500 text-2xl font-bold mb-6 pb-4 border-b border-white/10">
              <FaCalculator />
              <h3 className="text-lg font-black uppercase text-white">Enter Your Details</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                  <FaRulerVertical className="text-red-500" /> Height (cm)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 175"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  className="bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors text-sm"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-bold text-gray-300 uppercase tracking-wider flex items-center gap-2">
                  <FaWeight className="text-red-500" /> Weight (kg)
                </label>
                <input
                  type="number"
                  placeholder="e.g. 70"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors text-sm"
                />
              </div>
            </div>

            {error && <p className="text-red-400 text-xs mb-4">{error}</p>}

            <div className="flex gap-4 mb-8">
              <button
                onClick={calculate}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black py-3.5 rounded-xl transition-all shadow-lg shadow-red-600/30 cursor-pointer text-xs uppercase tracking-wider"
              >
                Calculate BMI
              </button>
              {bmi && (
                <button
                  onClick={reset}
                  className="bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3.5 rounded-xl transition-all cursor-pointer text-xs uppercase"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Reference Table */}
          <div className="pt-6 border-t border-white/10">
            <h4 className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-3">BMI Categories</h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {bmiCategories.map((cat, i) => (
                <div
                  key={i}
                  className={`flex items-center justify-between p-2.5 rounded-xl border transition-all ${
                    category && category.label === cat.label
                      ? 'bg-red-600/20 border-red-500 text-white font-bold'
                      : 'bg-white/5 border-white/5 text-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cat.color }} />
                    <span className="text-xs">{cat.label}</span>
                  </div>
                  <span className="text-gray-400 font-mono text-[10px]">{cat.range}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Result Card */}
        <motion.div
          className="bg-gradient-to-b from-[#181a24]/90 to-[#12141c]/90 backdrop-blur-xl p-8 rounded-3xl border border-white/10 shadow-2xl flex flex-col items-center justify-center text-center min-h-[380px]"
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={transition}
          viewport={{ once: true }}
        >
          <AnimatePresence mode="wait">
            {bmi ? (
              <motion.div
                key="result"
                className="w-full flex flex-col items-center"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
              >
                <h3 className="text-[10px] uppercase font-black tracking-widest text-gray-400 mb-4">Your BMI Score</h3>
                <div
                  className="w-32 h-32 rounded-full border-4 flex flex-col items-center justify-center mb-4 shadow-xl"
                  style={{ borderColor: category.color, backgroundColor: `${category.color}15` }}
                >
                  <span className="text-4xl font-extrabold text-white">{bmi}</span>
                  <span className="text-xs text-gray-300 font-semibold uppercase">BMI</span>
                </div>

                <div
                  className="px-6 py-1.5 rounded-full text-xs font-black uppercase tracking-wider mb-6 border"
                  style={{
                    backgroundColor: `${category.color}25`,
                    borderColor: category.color,
                    color: category.color,
                  }}
                >
                  {category.label}
                </div>

                {/* Gauge */}
                <div className="w-full mb-6 px-4">
                  <div className="w-full h-2.5 bg-white/10 rounded-full relative overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: category.color }}
                      initial={{ width: '0%' }}
                      animate={{ width: `${getGaugePercent(bmi)}%` }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                    />
                  </div>
                  <div className="flex justify-between text-[10px] text-gray-400 mt-2">
                    <span>Underweight</span>
                    <span>Normal</span>
                    <span>Overweight</span>
                    <span>Obese</span>
                  </div>
                </div>

                <p className="text-xs text-gray-300 leading-relaxed max-w-md bg-white/5 p-4 rounded-2xl border border-white/10">
                  {category.label === 'Normal'
                    ? '🎉 Great! Your weight is in the healthy range. Keep maintaining a balanced diet and regular exercise with our workout routines!'
                    : category.label === 'Underweight'
                    ? '💪 You may need to increase your calorie intake and start a structured strength training program. Talk to our coaches!'
                    : category.label === 'Overweight'
                    ? '🏃 A mix of cardio and progressive strength training with proper nutrition can help. Our coaches are here to guide you!'
                    : '⚠️ We recommend consulting our fitness coaches for a structured weight management program across our 3 units.'}
                </p>
              </motion.div>
            ) : (
              <motion.div
                key="placeholder"
                className="flex flex-col items-center text-center p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-5xl mb-4">⚖️</div>
                <h3 className="text-xl font-black uppercase text-white mb-2">Know Your BMI</h3>
                <p className="text-gray-300 text-xs sm:text-sm max-w-sm mb-6 leading-relaxed">
                  Enter your height and weight to discover your Body Mass Index and get a personalised recommendation from our team.
                </p>
                <div className="flex flex-col gap-2 text-xs text-gray-400 text-left">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500" />
                    <span>18.5–24.9 is the healthy range</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    <span>Regular strength training helps maintain it</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    <span>Our coaches at all 3 units can help you improve</span>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default BmiCalculator;

